import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Box } from '#src/atoms/icons/Box.tsx';
import { Cart } from '#src/atoms/icons/Cart.tsx';
import { Gavel } from '#src/atoms/icons/Gavel.tsx';
import { Swap } from '#src/atoms/icons/Swap.tsx';
import { Accordion } from '#src/atoms/layout/accordion/Accordion/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  BodyL,
  BodyM,
  BrandM,
  BrandXXL,
} from '#src/atoms/typography/index.tsx';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

import av1 from './assets/SORARE-101VIDEO.av1.webm';

const Root = styled.section`
  display: flex;
  padding: var(--quadruple-unit);
  align-items: flex-start;
  justify-content: space-around;
  @media ${laptopAndAbove} {
    padding: calc(var(--unit) * 15) calc(var(--unit) * 10);
    gap: var(--quadruple-unit);
    height: 100vh;
  }
`;

const TextContainer = styled(Vertical).attrs({
  gap: 6,
})`
  justify-content: flex-start;
  min-height: calc(var(--unit) * 93);
  @media ${laptopAndAbove} {
    width: 50%;
  }
`;

const VideoContainer = styled(Horizontal).attrs({
  center: true,
})`
  @media ${laptopAndAbove} {
    width: 50%;
    min-height: calc(var(--unit) * 93);
  }
`;

const AccordionTitle = styled(BrandM)`
  color: var(--c-nd-400-opaque);
  &.isOpen {
    color: var(--white);
  }
`;

export const ScoutAndCollect = () => {
  const isLaptop = useIsLaptop();
  const [openAccordionId, setOpenAccordionId] = useState<
    | 'win-auctions'
    | 'buy-instantly'
    | 'trade-directly'
    | 'earn-rewards'
    | undefined
  >('win-auctions');

  const accordionVideos = {
    'win-auctions': av1,
    'buy-instantly': av1,
    'trade-directly': av1,
    'earn-rewards': av1,
  };
  const [displayedVideo, setDisplayedVideo] = useState(
    accordionVideos['win-auctions']
  );

  const handleAccordionChange = (
    accordionId:
      | 'win-auctions'
      | 'buy-instantly'
      | 'trade-directly'
      | 'earn-rewards'
  ) => {
    if (openAccordionId === accordionId) {
      setOpenAccordionId(undefined);
    } else {
      setDisplayedVideo(accordionVideos[accordionId]);
      setOpenAccordionId(accordionId);
    }
  };

  return (
    <Root id="home">
      <TextContainer>
        <Vertical gap={4}>
          <Vertical gap={2}>
            <BrandM uppercase color="var(--c-limited)">
              <FormattedMessage defaultMessage="How to sign players" />
            </BrandM>
            <BrandXXL uppercase>
              <FormattedMessage defaultMessage="Scout and Collect" />
            </BrandXXL>
          </Vertical>
          <BodyL>
            <FormattedMessage defaultMessage="Build your team using officially licensed digital player cards from the NBA, MLB, the Premier League, Bundesliga, LALIGA EA Sports, Ligue 1, and more!" />
          </BodyL>
          <BodyL>
            <FormattedMessage defaultMessage="There are several ways to collect player cards, including:" />
          </BodyL>
        </Vertical>
        <Accordion
          expanded={openAccordionId === 'win-auctions'}
          onExpandChange={() => handleAccordionChange('win-auctions')}
          title={
            <Horizontal>
              <Gavel />
              <AccordionTitle
                className={classNames({
                  isOpen: openAccordionId === 'win-auctions',
                })}
              >
                <FormattedMessage defaultMessage="Win Auctions" />
              </AccordionTitle>
            </Horizontal>
          }
        >
          <Vertical gap={2}>
            <BodyM>
              <FormattedMessage defaultMessage="Bid on newly released cards from Sorare." />
            </BodyM>
            {!isLaptop && (
              <video width="100%" height="100%" muted autoPlay loop>
                <source src={accordionVideos['win-auctions']} />
              </video>
            )}
          </Vertical>
        </Accordion>
        <Accordion
          expanded={openAccordionId === 'buy-instantly'}
          onExpandChange={() => handleAccordionChange('buy-instantly')}
          title={
            <Horizontal>
              <Cart />
              <AccordionTitle
                className={classNames({
                  isOpen: openAccordionId === 'buy-instantly',
                })}
              >
                <FormattedMessage defaultMessage="Buy Instantly" />
              </AccordionTitle>
            </Horizontal>
          }
        >
          <Vertical gap={2}>
            <BodyM>
              <FormattedMessage defaultMessage="Bid on newly released cards from Sorare." />
            </BodyM>
            {!isLaptop && (
              <video width="100%" height="100%" muted autoPlay loop>
                <source src={accordionVideos['buy-instantly']} />
              </video>
            )}
          </Vertical>
        </Accordion>
        <Accordion
          expanded={openAccordionId === 'trade-directly'}
          onExpandChange={() => handleAccordionChange('trade-directly')}
          title={
            <Horizontal>
              <Swap color="#927AFF" />
              <AccordionTitle
                className={classNames({
                  isOpen: openAccordionId === 'trade-directly',
                })}
              >
                <FormattedMessage defaultMessage="Trade Directly" />
              </AccordionTitle>
            </Horizontal>
          }
        >
          <Vertical gap={2}>
            <BodyM>
              <FormattedMessage defaultMessage="Bid on newly released cards from Sorare." />
            </BodyM>
            {!isLaptop && (
              <video width="100%" height="100%" muted autoPlay loop>
                <source src={accordionVideos['trade-directly']} />
              </video>
            )}
          </Vertical>
        </Accordion>
        <Accordion
          expanded={openAccordionId === 'earn-rewards'}
          onExpandChange={() => handleAccordionChange('earn-rewards')}
          title={
            <Horizontal>
              <Box color="#927AFF" />
              <AccordionTitle
                className={classNames({
                  isOpen: openAccordionId === 'earn-rewards',
                })}
              >
                <FormattedMessage defaultMessage="Earn Rewards" />
              </AccordionTitle>
            </Horizontal>
          }
        >
          <Vertical gap={2}>
            <BodyM>
              <FormattedMessage defaultMessage="Bid on newly released cards from Sorare." />
            </BodyM>
            {!isLaptop && (
              <video width="100%" height="100%" muted autoPlay loop>
                <source src={accordionVideos['earn-rewards']} />
              </video>
            )}
          </Vertical>
        </Accordion>
      </TextContainer>
      {!!isLaptop && (
        <VideoContainer>
          <video
            key={displayedVideo}
            width="100%"
            height="100%"
            muted
            autoPlay
            loop
          >
            <source src={displayedVideo} />
          </video>
        </VideoContainer>
      )}
    </Root>
  );
};
